/**
 * ProceduralAircraftFactory
 *
 * Generates silhouette-accurate 3D aircraft shapes from physical dimensions
 * and category classification. Uses Three.js primitives — no external assets.
 *
 * Model orientation:
 *   - Nose → +Z axis
 *   - Up → +Y axis
 *   - Group origin → ground-level center of fuselage
 *
 * All input dimensions are in FEET. Internal geometry is built in meters.
 */

import * as THREE from 'three'
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js'
import { FEET_TO_METERS } from '@/constants/units'
import type { ProceduralAircraftParams } from './types'
import { getCategoryProfile } from './categoryProfiles'
import { getBodyMaterial, getAccentMaterial, getPropDiscMaterial } from './materials'
import { createFuselage } from './geometry/fuselageBuilder'
import { createWings } from './geometry/wingBuilder'
import { createStabilizers } from './geometry/stabilizerBuilder'
import { createEngines } from './geometry/engineBuilder'
import { createLandingGear } from './geometry/landingGearBuilder'
import { createHelicopter } from './geometry/helicopterBuilder'

export class ProceduralAircraftFactory {
  /**
   * Create a procedural aircraft THREE.Group from physical parameters.
   * Returns a group with origin at ground-center, nose at +Z.
   * Geometries are merged by material to minimize draw calls (2-3 per aircraft).
   */
  static create(params: ProceduralAircraftParams): THREE.Group {
    const profile = getCategoryProfile(params.category)

    // Helicopter is a completely different shape
    if (params.category === 'helicopter') {
      const heliGroup = createHelicopter(params, profile)
      // Merge helicopter geometries too (3 materials: body, accent, prop disc)
      return this.mergeHelicopterByMaterial(heliGroup)
    }

    return this.createFixedWing(params)
  }

  private static createFixedWing(params: ProceduralAircraftParams): THREE.Group {
    const profile = getCategoryProfile(params.category)
    const group = new THREE.Group()
    group.name = 'procedural-aircraft'

    // Convert master dimensions to meters
    const lengthM = params.length * FEET_TO_METERS
    const wingspanM = params.wingspan * FEET_TO_METERS
    const heightM = params.height * FEET_TO_METERS
    const tailHeightM = params.tailHeight
      ? params.tailHeight * FEET_TO_METERS
      : null

    // Derive secondary dimensions
    const fuselageDiameter = heightM * profile.fuselageDiameterRatio
    const fuselageRadius = fuselageDiameter / 2
    const noseLength = lengthM * profile.noseConeLengthRatio
    const tailTaperLength = lengthM * profile.tailTaperRatio
    const fuselageLength = lengthM // total tip-to-tail

    // Materials (cached singletons)
    const bodyMat = getBodyMaterial()
    const accentMat = getAccentMaterial()

    // 1. Fuselage
    const fuselage = createFuselage(
      fuselageLength, fuselageRadius, noseLength, tailTaperLength, profile, bodyMat
    )
    group.add(fuselage)

    // 2. Wings
    const wingChord = wingspanM * profile.wingChordRatio
    const wingThickness = wingChord * 0.12
    // Wing Y position relative to fuselage center
    const wingY = fuselageRadius * profile.wingVerticalOffset
    // Wing Z position: offset from fuselage center based on wingPositionRatio
    // 0.5 = center, <0.5 = forward, >0.5 = aft
    const wingZ = fuselageLength * (0.5 - profile.wingPositionRatio)

    const wings = createWings(
      wingspanM, wingChord, wingThickness, wingY, wingZ, profile, bodyMat
    )
    group.add(wings)

    // 3. Stabilizers
    const hStabSpan = wingspanM * profile.hStabSpanRatio
    const hStabChord = wingChord * profile.hStabChordRatio
    const vStabHeight = tailHeightM
      ? tailHeightM - fuselageRadius
      : lengthM * profile.vStabHeightFallbackRatio
    // Tail at rear of fuselage
    const tailZ = -(fuselageLength / 2) + hStabChord

    const stabilizers = createStabilizers(
      hStabSpan, hStabChord, vStabHeight, fuselageRadius, tailZ, profile, bodyMat
    )
    group.add(stabilizers)

    // 4. Engines
    if (profile.engineCount > 0) {
      const engines = createEngines(
        profile, fuselageRadius, fuselageLength, wingspanM, wingY, wingZ, accentMat
      )
      group.add(engines)
    }

    // 5. Landing gear
    if (profile.showGear) {
      const gear = createLandingGear(
        fuselageRadius, fuselageLength, wingspanM, accentMat
      )
      group.add(gear)
    }

    // Position the whole group so origin is at ground level.
    // Fuselage centerline is at y=0 in the group, so we need to lift by fuselageRadius
    // plus gear strut height to sit on the ground.
    const gearClearance = profile.showGear ? fuselageRadius * 0.8 : 0
    group.position.y = fuselageRadius + gearClearance

    // Merge geometries by material to reduce draw calls
    return this.mergeByMaterial(group, bodyMat, accentMat)
  }

  /**
   * Merge all meshes in a group by material to reduce draw calls.
   * Returns an optimized group with 2-3 meshes instead of many.
   * Preserves the group's position/rotation/scale as a movable container.
   */
  private static mergeByMaterial(
    group: THREE.Group,
    bodyMat: THREE.MeshStandardMaterial,
    accentMat: THREE.MeshStandardMaterial
  ): THREE.Group {
    const discMat = getPropDiscMaterial()
    const bodyGeometries: THREE.BufferGeometry[] = []
    const accentGeometries: THREE.BufferGeometry[] = []
    const discGeometries: THREE.BufferGeometry[] = []

    // Save and temporarily zero the group's position so it doesn't
    // get baked into the merged geometries. We want geometries in
    // group-local space, not world space.
    const savedPosition = group.position.clone()
    group.position.set(0, 0, 0)
    group.updateMatrixWorld(true)

    // Collect all mesh geometries, applying their transforms relative to group
    group.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry) {
        // Clone geometry and apply mesh's world transform (now relative to group origin)
        const geo = child.geometry.clone()
        geo.applyMatrix4(child.matrixWorld)

        // Sort by material
        if (child.material === bodyMat) {
          bodyGeometries.push(geo)
        } else if (child.material === accentMat) {
          accentGeometries.push(geo)
        } else if (child.material === discMat) {
          discGeometries.push(geo)
        } else {
          // Unknown material - treat as body
          bodyGeometries.push(geo)
        }
      }
    })

    // Restore group position
    group.position.copy(savedPosition)

    // Create optimized group
    const optimized = new THREE.Group()
    optimized.name = group.name

    // Merge body geometries (typically: fuselage, wings, stabilizers)
    if (bodyGeometries.length > 0) {
      const mergedBody = mergeGeometries(bodyGeometries)
      if (mergedBody) {
        const bodyMesh = new THREE.Mesh(mergedBody, bodyMat)
        bodyMesh.castShadow = true
        bodyMesh.receiveShadow = true
        bodyMesh.name = 'merged-body'
        optimized.add(bodyMesh)
      }
      bodyGeometries.forEach((g) => g.dispose())
    }

    // Merge accent geometries (typically: engines, landing gear)
    if (accentGeometries.length > 0) {
      const mergedAccent = mergeGeometries(accentGeometries)
      if (mergedAccent) {
        const accentMesh = new THREE.Mesh(mergedAccent, accentMat)
        accentMesh.castShadow = true
        accentMesh.receiveShadow = true
        accentMesh.name = 'merged-accent'
        optimized.add(accentMesh)
      }
      accentGeometries.forEach((g) => g.dispose())
    }

    // Merge prop disc geometries (piston/turboprop aircraft)
    if (discGeometries.length > 0) {
      const mergedDisc = mergeGeometries(discGeometries)
      if (mergedDisc) {
        const discMesh = new THREE.Mesh(mergedDisc, discMat)
        discMesh.name = 'merged-disc'
        // Discs don't need shadows (translucent spinning prop)
        optimized.add(discMesh)
      }
      discGeometries.forEach((g) => g.dispose())
    }

    // Restore group's position (for ground clearance offset)
    optimized.position.copy(savedPosition)

    return optimized
  }

  /**
   * Merge helicopter geometries by material (body, accent, prop disc).
   * Similar to mergeByMaterial but handles 3 material types.
   */
  private static mergeHelicopterByMaterial(group: THREE.Group): THREE.Group {
    const bodyMat = getBodyMaterial()
    const accentMat = getAccentMaterial()
    const discMat = getPropDiscMaterial()

    const bodyGeometries: THREE.BufferGeometry[] = []
    const accentGeometries: THREE.BufferGeometry[] = []
    const discGeometries: THREE.BufferGeometry[] = []

    // Helicopter groups typically don't have position offset, but be safe
    const savedPosition = group.position.clone()
    group.position.set(0, 0, 0)
    group.updateMatrixWorld(true)

    group.traverse((child) => {
      if (child instanceof THREE.Mesh && child.geometry) {
        const geo = child.geometry.clone()
        geo.applyMatrix4(child.matrixWorld)

        if (child.material === bodyMat) {
          bodyGeometries.push(geo)
        } else if (child.material === accentMat) {
          accentGeometries.push(geo)
        } else if (child.material === discMat) {
          discGeometries.push(geo)
        } else {
          bodyGeometries.push(geo)
        }
      }
    })

    group.position.copy(savedPosition)

    const optimized = new THREE.Group()
    optimized.name = group.name

    if (bodyGeometries.length > 0) {
      const merged = mergeGeometries(bodyGeometries)
      if (merged) {
        const mesh = new THREE.Mesh(merged, bodyMat)
        mesh.castShadow = true
        mesh.receiveShadow = true
        mesh.name = 'merged-body'
        optimized.add(mesh)
      }
      bodyGeometries.forEach((g) => g.dispose())
    }

    if (accentGeometries.length > 0) {
      const merged = mergeGeometries(accentGeometries)
      if (merged) {
        const mesh = new THREE.Mesh(merged, accentMat)
        mesh.castShadow = true
        mesh.receiveShadow = true
        mesh.name = 'merged-accent'
        optimized.add(mesh)
      }
      accentGeometries.forEach((g) => g.dispose())
    }

    if (discGeometries.length > 0) {
      const merged = mergeGeometries(discGeometries)
      if (merged) {
        const mesh = new THREE.Mesh(merged, discMat)
        mesh.name = 'merged-disc'
        // Discs don't need shadows (translucent rotor)
        optimized.add(mesh)
      }
      discGeometries.forEach((g) => g.dispose())
    }

    optimized.position.copy(savedPosition)
    return optimized
  }
}
